import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Grid, Checkbox, TextField, Button } from '@mui/material';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import FavoriteIcon from '@mui/icons-material/Favorite';
import MedicationIcon from '@mui/icons-material/Medication';
import ScienceIcon from '@mui/icons-material/Science';
import EventNoteIcon from '@mui/icons-material/EventNote';
import GroupIcon from '@mui/icons-material/Group';
import FlowsheetService from '../../services/flowsheetService';

export default function FlowsheetModal({ open, onClose, flowsheet, encounterId, onSave }) {
  const showLabs = flowsheet ? flowsheet.trigger === 'Lab Result' : false;
  const showScheduledProcedures = flowsheet ? flowsheet.trigger === 'Manual' : false;
  const [form, setForm] = useState(flowsheet || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const handleCheckbox = (field) => setForm(f => ({ ...f, [field]: !f[field] }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const flowsheetService = new FlowsheetService();
      
      if (flowsheet && flowsheet.id) {
        // Edit mode: update existing
        await flowsheetService.updateFlowsheet(flowsheet.encounterId, flowsheet.id, form);
        setSuccess('Flowsheet updated.');
      } else {
        // Create mode: add new
        if (!encounterId) throw new Error('No encounter selected');
        await flowsheetService.createFlowsheet(encounterId, form);
        setSuccess('Flowsheet created.');
      }
      if (onSave) onSave();
      onClose();
    } catch (err) {
      setError('Failed to save flowsheet.');
    } finally {
      setSaving(false);
    }
  };

  const sectionHeader = (icon, color, label) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, background: color, color: '#fff', px: 2, py: 0.5, borderRadius: 1 }}>
      {icon}
      <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600 }}>{label}</Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{flowsheet ? 'Edit Flowsheet' : 'New Flowsheet'}</DialogTitle>
      <DialogContent dividers sx={{ pb: 0 }}>
        {/* ASSESSMENTS */}
        {sectionHeader(<AssignmentIndIcon />, '#607d8b', 'ASSESSMENTS')}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {['oriented','confused','unconscious','reflex','power','aox','q4check','rass','neuroChecks'].map(field => (
            <Grid item xs={6} sm={4} md={3} key={field}>
              <Checkbox checked={!!form[field]} onChange={() => handleCheckbox(field)} />
              <Typography variant="body2" component="span">{field.charAt(0).toUpperCase() + field.slice(1)}</Typography>
            </Grid>
          ))}
        </Grid>
        {/* VITALS */}
        {sectionHeader(<FavoriteIcon />, '#1976d2', 'VITALS')}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {['temp','bp','hr','pr','spo2'].map(field => (
            <Grid item xs={6} sm={4} md={2} key={field}>
              <TextField label={field.toUpperCase()} size="small" value={form[field] || ''} onChange={e => handleChange(field, e.target.value)} fullWidth />
            </Grid>
          ))}
        </Grid>
        {/* MEDICATIONS & TO DO */}
        {sectionHeader(<MedicationIcon />, '#8bc34a', 'MEDICATIONS & TO DO')}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4} md={3}><TextField label="Time" size="small" value={form.medTime || ''} onChange={e => handleChange('medTime', e.target.value)} fullWidth /></Grid>
          <Grid item xs={6} sm={8} md={9}><TextField label="Task" size="small" value={form.medTask || ''} onChange={e => handleChange('medTask', e.target.value)} fullWidth /></Grid>
        </Grid>
        {/* ACCU CHECK */}
        {sectionHeader(<ScienceIcon />, '#ff9800', 'ACCU CHECK')}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {['ac','hs','hourly'].map(field => (
            <Grid item xs={4} sm={2} key={field}>
              <Checkbox checked={!!form[field]} onChange={() => handleCheckbox(field)} />
              <Typography variant="body2" component="span">{field.toUpperCase()}</Typography>
            </Grid>
          ))}
          {['accuTime','bs','cover'].map(field => (
            <Grid item xs={4} sm={2} key={field}>
              <TextField label={field.toUpperCase()} size="small" value={form[field] || ''} onChange={e => handleChange(field, e.target.value)} fullWidth />
            </Grid>
          ))}
        </Grid>
        {/* LABS (conditionally rendered) */}
        {showLabs && (
          <>
            {sectionHeader(<ScienceIcon />, '#43a047', 'LABS')}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {['hgb','wbc','plt','ptinr','bun','cr','ph','na','k','mg','ca','troponin'].map(field => (
                <Grid item xs={6} sm={4} md={2} key={field}>
                  <TextField label={field.toUpperCase()} size="small" value={form[field] || ''} onChange={e => handleChange(field, e.target.value)} fullWidth />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {/* PLANNING */}
        {sectionHeader(<EventNoteIcon />, '#ab47bc', 'PLANNING')}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}><TextField label="Plan of Care" size="small" value={form.planOfCare || ''} onChange={e => handleChange('planOfCare', e.target.value)} fullWidth /></Grid>
          <Grid item xs={12} sm={6}><TextField label="Discharge Plan" size="small" value={form.dischargePlan || ''} onChange={e => handleChange('dischargePlan', e.target.value)} fullWidth /></Grid>
        </Grid>
        {/* Scheduled Procedures (conditionally rendered) */}
        {showScheduledProcedures && (
          <>
            {sectionHeader(<ScienceIcon />, '#ff7043', 'SCHEDULED PROCEDURES')}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {['cath','echo','ekg','pacemaker','mri','xray','ct','usdopplers','boneScan','mammography'].map(field => (
                <Grid item xs={6} sm={4} md={2} key={field}>
                  <Checkbox checked={!!form[field]} onChange={() => handleCheckbox(field)} />
                  <Typography variant="body2" component="span">{field.charAt(0).toUpperCase() + field.slice(1)}</Typography>
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {/* CONSULTS */}
        {sectionHeader(<GroupIcon />, '#0097a7', 'CONSULTS')}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {['psych','neuro','ortho','ptot','nephro','pulmo','medi','urology','speech','surg','cardio'].map(field => (
            <Grid item xs={6} sm={4} md={2} key={field}>
              <Checkbox checked={!!form[field]} onChange={() => handleCheckbox(field)} />
              <Typography variant="body2" component="span">{field.charAt(0).toUpperCase() + field.slice(1)}</Typography>
            </Grid>
          ))}
        </Grid>
        {error && <Typography color="error" sx={{ mb: 1 }}>{error}</Typography>}
        {success && <Typography color="success.main" sx={{ mb: 1 }}>{success}</Typography>}
      </DialogContent>
      <DialogActions sx={{ position: 'sticky', bottom: 0, background: '#fff', zIndex: 1 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 